'use client'

import React, { useContext } from 'react'
import { TableContext } from '../../../../providers'
import { TableSizes } from '../../../../types'
import { Column, TableHeaderVariants } from '../../../../types/Table'
import { ComponentTranslationContext } from '../../../../providers'
import { useStickyColumn } from '../hooks/UseStickyColumn'
import { SortingDirection } from '../../../../constants/sortingConstants'
import { TableHeaderCellIcon } from './TableHeaderCellIcon'
import { Tooltip } from '../../../atoms'
import { MESSAGE_KEYS } from '../../../../constants/messageKeys'

export interface TableHeaderCellProps {
    children?: React.ReactNode
    column?: Column
}

export const TableHeaderCell = ({ children, column }: TableHeaderCellProps): JSX.Element => {
    const { sortHandler, isNested, isInline, isStretched, size, headerVariant } = useContext(TableContext)
    const { getLabel } = useContext(ComponentTranslationContext)
    const { ref, styling, getStickyClasses } = useStickyColumn(column)
    const rootClass = 'table-header-cell'

    const onSort = () => {
        if (!column?.sortBy || !sortHandler) return
        sortHandler(column?.sortBy)
    }

    const isSortable: boolean = !!column?.sortBy && !!sortHandler
    const isSorted: boolean = !!column?.sortDirection && column?.sortDirection !== SortingDirection.NONE

    const getClasses = (): string => {
        let classes = rootClass

        if (headerVariant === TableHeaderVariants.QUIET) classes += ` ${rootClass}--quiet`

        switch (column?.alignment) {
            case 'start':
                classes += ` ${rootClass}--start-aligned`
                break
            case 'center':
                classes += ` ${rootClass}--center-aligned`
                break
            case 'end':
                classes += ` ${rootClass}--end-aligned`
                break
        }
        switch (size) {
            case TableSizes.SMALL:
                classes += ` ${rootClass}--small`
                break
            case TableSizes.MEDIUM:
                classes += ` ${rootClass}--medium`
                break
            case TableSizes.LARGE:
                classes += ` ${rootClass}--large`
                break
        }
        if (column?.isActionColumn) classes += ` ${rootClass}--is-actions-cell`
        if (isSortable) classes += ` ${rootClass}--sortable`
        if (isSorted) classes += ` ${rootClass}--sorted`
        if (isNested) classes += ` ${rootClass}--nested`
        if (isInline) classes += ` ${rootClass}--inline`
        if (isStretched) classes += ` ${rootClass}--stretched`
        classes += ` ${getStickyClasses(rootClass)}`
        return classes
    }

    const getAlignmentClass = (): string => {
        switch (column?.alignment) {
            default:
            case 'start':
                return 'justify-start'
            case 'center':
                return 'justify-center'
            case 'end':
                return 'justify-end'
        }
    }

    /** Empty value is either "acties" for the action column OR a space.
     * It has to be a space because of table-behaviour in the browser, without the space the design is off.
     */
    const EMPTY_CELL_VALUE = <>&nbsp;</>

    const tooltip: JSX.Element = column?.tooltip ? <Tooltip content={column.tooltip} filled={true} /> : <></>

    return (
        <th className={getClasses()} style={styling} ref={ref}>
            <div className={`${rootClass}__content`} onClick={onSort}>
                <div className={`flex items-center gap-1 w-full ${getAlignmentClass()}`}>
                    {(isSortable || column?.filterBy) && (
                        <>
                            <p>{children || EMPTY_CELL_VALUE}</p>
                            {tooltip}
                            <TableHeaderCellIcon column={column} />
                        </>
                    )}
                    {!isSortable && !column?.filterBy && (
                        <>
                            <h5>
                                {children || EMPTY_CELL_VALUE}
                                {column?.isActionColumn && (
                                    <span className={column?.isActionColumn && !children ? 'sr-only' : ''}>
                                        {getLabel(MESSAGE_KEYS.TABLE_ACTIONS)}
                                    </span>
                                )}
                            </h5>
                            {tooltip}
                        </>
                    )}
                </div>
            </div>
        </th>
    )
}
